import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { CreateAppPayload, UpdateAppPayload } from "@web-app/types";

import { create, getAppsByIds, list, update } from "../services/app";

const queryKey = "apps";

export const useApps = (workspaceId: string) => {
  return useQuery({
    queryFn: async () => {
      const response = await list(workspaceId);
      return response;
    },
    queryKey: [`${queryKey}`],
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60 * 24, // 24 Hours
  });
};

export const useGetAppsByIds = (appIds: string[]) => {
  return useQuery({
    queryFn: async () => getAppsByIds(appIds),
    queryKey: [queryKey, "byIds", appIds.sort().join(",")],
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
};

export const useAppMutation = () => {
  const queryClient = useQueryClient();

  const createApp = useMutation({
    mutationFn: async (payload: CreateAppPayload) => {
      const response = await create(payload);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
    },
  });

  const updateApp = useMutation({
    mutationFn: async (params: {
      id: string;
      payload: Partial<UpdateAppPayload>;
    }) => {
      const response = await update(params.id, params.payload);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
    },
  });

  return {
    createApp,
    updateApp,
  };
};
